import { Platform } from "react-native";

import {
  createWebRiseDismissalReplayGate,
  getWebFocusedRiseLayerDismissal,
  getWebNavigationDirection,
  getWebNavigationFadeReveal,
  recordWebHistoryTraversal,
  recordWebNavigationIntent,
  releaseWebNavigationFocus
} from "@/design/navigationMotionState";

/**
 * Every history entry this app creates is stamped with a monotonic index,
 * stored inside history.state — the browser persists it per entry, including
 * across reloads. Back/Forward direction is then simply the sign of the index
 * delta observed on popstate. No mirroring of history contents, which the
 * browser never fully exposes and which drifts out of sync.
 */
const HISTORY_INDEX_KEY = "__recitalHistoryIndex";

function readIndex(state: unknown): number | null {
  if (!state || typeof state !== "object") return null;
  const value = (state as Record<string, unknown>)[HISTORY_INDEX_KEY];
  return typeof value === "number" ? value : null;
}

function stamped(state: unknown, index: number): object {
  const base = state && typeof state === "object" ? state : {};
  return { ...base, [HISTORY_INDEX_KEY]: index };
}

// Install exactly once per page, not per module execution: dev-server hot
// reloads re-run this module, and stacking a second history patch plus a
// second set of listeners would double-process every navigation.
const installScope = globalThis as { __recitalNavMotionWired?: boolean };

if (
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  !installScope.__recitalNavMotionWired
) {
  installScope.__recitalNavMotionWired = true;

  // An entry without a stamp (first visit, or created outside this app) sits
  // at the tail of the tab's history; length - 1 is its true position, which
  // keeps deltas against stamps persisted by earlier sessions correct.
  let currentIndex =
    readIndex(window.history.state) ?? Math.max(0, window.history.length - 1);

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  if (readIndex(window.history.state) == null) {
    originalReplaceState(stamped(window.history.state, currentIndex), "");
  }

  window.history.pushState = (data, unused, url) => {
    recordWebNavigationIntent("PUSH");
    currentIndex += 1;
    originalPushState(stamped(data, currentIndex), unused, url);
  };
  window.history.replaceState = (data, unused, url) => {
    originalReplaceState(stamped(data, currentIndex), unused, url);
  };

  // This listener registers at module evaluation — before React Navigation
  // mounts and attaches its own popstate listener — so it always runs first
  // and may withhold an event from the router via stopImmediatePropagation.
  const riseDismissalReplay = createWebRiseDismissalReplayGate();

  window.addEventListener("popstate", (event) => {
    releaseWebNavigationFocus();
    const index = readIndex(event.state);
    const delta = index == null ? null : index - currentIndex;

    // Browser Back while a raised layer is focused: the router would replay
    // this traversal as a resetRoot, which (depending on which navigator the
    // recorded state targets) can replace the nested stack wholesale — no
    // beforeRemove, no exit animation, the layer just snaps out. So the
    // traversal is withheld from the router entirely: the layer plays its
    // reverse-rise first, then the popstate is re-dispatched for the router
    // to consume. The browser's history has already moved either way.
    if (delta != null && delta < 0) {
      const dismiss = getWebFocusedRiseLayerDismissal();
      if (dismiss && riseDismissalReplay.tryBegin()) {
        event.stopImmediatePropagation();
        dismiss(() => {
          // A further traversal may have raced past us and already synced
          // the router (clearing the flag) — replaying a stale entry then
          // would rewind it, so only replay while still pending.
          riseDismissalReplay.replayIfPending(() => {
            window.dispatchEvent(
              new PopStateEvent("popstate", { state: window.history.state })
            );
          });
        });
        return;
      }
    }

    riseDismissalReplay.cancel();
    recordWebHistoryTraversal(delta);
    if (index != null) currentIndex = index;
  });
}

export { getWebNavigationDirection, getWebNavigationFadeReveal };
